// D:\directiveai-react\frontend\src\components\AGTUps\products\AGTCashFlow-products\Design-DuesPayments\TransactionSetupWizard.js

import React, { useState, useCallback, useMemo, useEffect, Suspense } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Tooltip,
  LinearProgress,
  Fade,
  Paper,
  Grid,
  InputAdornment,
} from '@mui/material';
import { styled, darken } from '@mui/material/styles';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { FileText, Tag, ArrowUpRight, ArrowDownRight, DollarSign } from 'lucide-react';

// Base color for the button
const blue = '#007BFE';

// ----- CUSTOM STYLES -----

// Dialog without shadow and with rounded borders
const StyledDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiDialog-paper': {
      borderRadius: '28px',
      padding: theme.spacing(2),
      overflow: 'hidden',
      boxShadow: 'none',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
      backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
    }
  };
});

// Content of each step with transition animation
const StepContent = styled(DialogContent)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  transition: 'all 0.3s ease-in-out',
}));

// Step indicator container
const StepIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  gap: theme.spacing(1),
}));

// Dot for each step (with active and completed state)
const StepDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'completed',
})(({ theme, active, completed }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: active
    ? (theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main)
    : completed
      ? (theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.6)' : theme.palette.primary.light)
      : (theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.3)' : theme.palette.grey[300]),
  transition: 'all 0.3s ease-in-out',
  transform: active ? 'scale(1.2)' : 'scale(1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

// Tooltip without shadow
const NoShadowTooltip = styled(Tooltip)(() => ({
  '& .MuiTooltip-tooltip': {
    boxShadow: 'none',
  },
}));

// ----- SUMMARY COMPONENT -----

const SummaryContent = ({ steps, transactionData }) => {
  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'text.primary'
        }}
      >
        Transaction Summary
      </Typography>
      <Grid container spacing={2}>
        {steps.map((step, index) => (
          <Grid item xs={12} key={index}>
            <Paper
              sx={{
                p: 2,
                borderRadius: '16px',
                boxShadow: 'none',
                backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(128, 128, 128, 0.25)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'),
                backdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                WebkitBackdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                border: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? '1px solid rgba(128, 128, 128, 0.3)'
                  : 'none'
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main
                }}
              >
                {step.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.8)'
                    : 'text.secondary'
                }}
              >
                {step.getValue ? step.getValue(transactionData) : 'Not provided'}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ----- TRANSACTION SETUP WIZARD COMPONENT -----

/**
 * Props:
 * - open: boolean to show/hide the dialog
 * - onClose: function to close the dialog
 * - initialData: object with initial data (for edit or creation)
 * - onSubmit: async function that will be called when the wizard is completed
 * - mode: "create" | "edit" to identify the flow
 */
const TransactionSetupWizard = ({ open, onClose, initialData = {}, onSubmit, mode = 'create' }) => {
  // Local state to store transaction data, including a "status" field (default: "pending")
  const [transactionData, setTransactionData] = useState({
    title: '',
    amount: '',
    date: '',
    type: 'expense', // 'income' or 'expense'
    category: 'office', // default category; customize as needed
    description: '',
    status: 'pending',
    // Additional fields can be added as required
    ...initialData,
  });

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // State for past date confirmation modal
  const [confirmPastDateOpen, setConfirmPastDateOpen] = useState(false);

  // Reset the wizard when closing
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setShowSummary(false);
      setConfirmPastDateOpen(false);
      setTransactionData({
        title: '',
        amount: '',
        date: '',
        type: 'expense',
        category: 'office',
        description: '',
        status: 'pending',
        ...initialData,
      });
    }
  }, [open, initialData]);

  // Handle field changes
  const handleChange = useCallback((e) => {
    const { name, value, type: inputType, checked } = e.target;
    setTransactionData((prev) => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : value,
    }));
  }, []);

  // Define base steps (without the summary step)
  const baseSteps = useMemo(() => [
    {
      title: 'Basic Information',
      description: 'Enter the transaction title, amount, and date.',
      required: true,
      // Now requiring all three fields: title, amount, and date
      field: ['title', 'amount', 'date'],
      getValue: (data) => data.title || 'Not provided',
      component: (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Transaction Title"
              name="title"
              value={transactionData.title}
              onChange={handleChange}
              required
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : undefined
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Amount"
              name="amount"
              type="number"
              value={transactionData.amount}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DollarSign style={{ marginRight: 8, color: 'inherit' }} />
                  </InputAdornment>
                ),
              }}
              required
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : undefined
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date"
              name="date"
              type="date"
              value={transactionData.date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : undefined
                }
              }}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      title: 'Additional Details',
      description: 'Define transaction type, category, and description.',
      required: true,
      field: 'type',
      getValue: (data) =>
        `Type: ${data.type}, Category: ${data.category}`,
      component: (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required sx={{ mt: 2 }}>
              <InputLabel
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                Type
              </InputLabel>
              <Select
                label="Type"
                name="type"
                value={transactionData.type}
                onChange={handleChange}
                sx={{
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.3)'
                      : undefined
                  },
                  '& .MuiSvgIcon-root': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                <MenuItem value="income">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArrowUpRight style={{ fontSize: 16 }} />
                    Income
                  </Box>
                </MenuItem>
                <MenuItem value="expense">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArrowDownRight style={{ fontSize: 16 }} />
                    Expense
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required sx={{ mt: 2 }}>
              <InputLabel
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                Category
              </InputLabel>
              <Select
                label="Category"
                name="category"
                value={transactionData.category}
                onChange={handleChange}
                sx={{
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.3)'
                      : undefined
                  },
                  '& .MuiSvgIcon-root': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                <MenuItem value="office">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tag style={{ fontSize: 16 }} />
                    Office
                  </Box>
                </MenuItem>
                <MenuItem value="software">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileText style={{ fontSize: 16 }} />
                    Software
                  </Box>
                </MenuItem>
                {/* Add more categories as needed */}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              multiline
              rows={3}
              value={transactionData.description}
              onChange={handleChange}
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : undefined
                }
              }}
            />
          </Grid>
        </Grid>
      ),
    },
  ], [transactionData, handleChange]);

  // Complete steps array (including the summary step)
  const steps = useMemo(() => [
    ...baseSteps,
    {
      title: 'Review Transaction',
      description: 'Review details before submitting.',
      required: false,
      getValue: () => 'Review entered information',
      component: (
        <Suspense fallback={<Box sx={{ p: 3 }}>Loading summary...</Box>}>
          <SummaryContent steps={baseSteps} transactionData={transactionData} />
        </Suspense>
      ),
    },
  ], [baseSteps, transactionData]);

  // Validate current step (updated to handle array of required fields)
  const validateStep = useCallback((stepIndex) => {
    const step = stepIndex < baseSteps.length ? baseSteps[stepIndex] : true;
    if (!step || !step.required) return true;
    if (Array.isArray(step.field)) {
      return step.field.every(
        (field) => transactionData[field] !== undefined && transactionData[field] !== ''
      );
    }
    return transactionData[step.field] !== undefined && transactionData[step.field] !== '';
  }, [baseSteps, transactionData]);

  // Handle step transition
  const handleStepTransition = useCallback((nextStep) => {
    setTransitioning(true);
    setTimeout(() => {
      setActiveStep(nextStep);
      setTransitioning(false);
    }, 300);
  }, []);

  // Handle next step (or submit)
  const handleNext = useCallback(async () => {
    if (!validateStep(activeStep)) return;
    // If we are at the penultimate step, check for past date confirmation
    if (activeStep === baseSteps.length - 1 && !showSummary) {
      const todayString = new Date().toISOString().split('T')[0];
      if (transactionData.date < todayString) {
        // Show confirmation modal if the selected date is before today.
        setConfirmPastDateOpen(true);
        return;
      }
      setShowSummary(true);
      return;
    }
    // If summary is shown, submit the form and close the modal upon success
    if (showSummary || activeStep === steps.length - 1) {
      setIsSubmitting(true);
      try {
        await onSubmit(transactionData);
        onClose();
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    handleStepTransition(activeStep + 1);
  }, [
    activeStep,
    baseSteps.length,
    showSummary,
    onSubmit,
    transactionData,
    validateStep,
    handleStepTransition,
    steps.length,
    onClose,
  ]);

  // Handle back
  const handleBack = useCallback(() => {
    if (showSummary) {
      setShowSummary(false);
      return;
    }
    handleStepTransition(activeStep - 1);
  }, [activeStep, showSummary, handleStepTransition]);

  // Capture Enter key to advance (preventing newline)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext]);

  return (
    <>
      <StyledDialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        {/* Progress bar and step counter */}
        <Box sx={{ width: '100%', mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={((activeStep + (showSummary ? 1 : 0)) / steps.length) * 100}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(0,123,254,0.12)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : blue
              }
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            sx={{ display: 'block', mt: 0.5 }}
          >
            Step {activeStep + 1} of {steps.length}
          </Typography>
        </Box>

        {/* Step indicator */}
        <StepIndicator>
          {steps.map((step, index) => (
            <NoShadowTooltip key={index} title={step.title}>
              <StepDot
                active={index === activeStep}
                completed={index < activeStep}
                onClick={() => {
                  if (index < activeStep) handleStepTransition(index);
                }}
              />
            </NoShadowTooltip>
          ))}
        </StepIndicator>

        <DialogTitle>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            {showSummary ? 'Review Transaction' : steps[activeStep].title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {showSummary
              ? 'Review the transaction details before submitting'
              : steps[activeStep].description}
          </Typography>
        </DialogTitle>

        <StepContent sx={{ opacity: transitioning ? 0 : 1 }}>
          {showSummary ? steps[steps.length - 1].component : steps[activeStep].component}
        </StepContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          {(activeStep > 0 || showSummary) && (
            <Button
              onClick={handleBack}
              startIcon={
                <ArrowBack
                  sx={{
                    color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : blue
                  }}
                />
              }
              sx={{
                textTransform: 'none',
                borderRadius: '28px',
                color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : blue,
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              Back
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            disableElevation
            onClick={handleNext}
            disabled={!validateStep(activeStep) || isSubmitting}
            endIcon={!showSummary && <ArrowForward />}
            sx={{
              textTransform: 'none',
              borderRadius: '30px',
              backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : blue,
              color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#000000' : 'white',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.85)'
                  : darken(blue, 0.2),
                boxShadow: 'none',
              },
            }}
          >
            {isSubmitting
              ? mode === 'create'
                ? 'Creating Transaction...'
                : 'Saving Changes...'
              : showSummary
              ? mode === 'create'
                ? 'Create Transaction'
                : 'Save Changes'
              : activeStep === baseSteps.length - 1
              ? 'Review'
              : 'Continue'}
          </Button>
        </DialogActions>
      </StyledDialog>

      {/* Modal de confirmación para fechas pasadas con el mismo estilo */}
      <StyledDialog
        open={confirmPastDateOpen}
        onClose={() => setConfirmPastDateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Confirm Past Payment
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            The date you selected is in the past. Does this indicate that you have already completed the payment on that date? Please confirm if you have paid, so we can register this transaction as complete. If not, please correct the date.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmPastDateOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: '28px',
              color: blue,
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmPastDateOpen(false);
              // Update the status to completed if the user confirms the past payment.
              setTransactionData(prev => ({ ...prev, status: 'completed' }));
              setShowSummary(true);
            }}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: '30px',
              backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : blue,
              color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#000000' : 'white',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.85)'
                  : darken(blue, 0.2),
                boxShadow: 'none',
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </StyledDialog>
    </>
  );
};

export default TransactionSetupWizard;
